import { useEffect, useRef, useState } from 'react';

/**
 * Call `fn` every `intervalMs` while the page is visible, and straight away when
 * it becomes visible again. Paused entirely while hidden, like the room poll.
 * Used for slow, cheap refreshes: a comic's title, the gallery list.
 */
export function useVisiblePoll(fn: () => unknown, intervalMs: number, enabled = true): void {
  const fnRef = useRef(fn);
  fnRef.current = fn;

  useEffect(() => {
    if (!enabled) return;
    const tick = () => {
      if (document.visibilityState === 'visible') void fnRef.current();
    };
    const timer = window.setInterval(tick, intervalMs);
    const onVisible = () => {
      if (document.visibilityState === 'visible') void fnRef.current();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [intervalMs, enabled]);
}

/** The current time, refreshed every `intervalMs` while `active` — for ticking timers. */
export function useNow(active: boolean, intervalMs = 1000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return;
    setNow(Date.now());
    const timer = window.setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(timer);
  }, [active, intervalMs]);
  return now;
}
